import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import {
  Button,
  FormField,
  Input,
  Eyebrow,
  Heading,
  Alert,
} from '@insolech/ui';
import { supabase } from '@/lib/supabase';
import { useAuth } from './AuthContext';

/** R12 — link from auth → marketing site. Standard B2B SaaS pattern
 *  (Linear, Vercel, Stripe all wire the auth-page logo back to marketing).
 *  Uses VITE_MARKETING_URL env var, falls back to the staging Vercel
 *  preview URL until production DNS cutover.
 */
const marketingUrl =
  import.meta.env.VITE_MARKETING_URL ?? 'https://insolech-marketing-insolech.vercel.app';

/**
 * AuthPage — invitation-only sign-in.
 *
 * Round 11 simplification (Rafael, 2026-05-08):
 *   - Removed the magic-link tab. Only password sign-in remains, so the
 *     tabbed layout is gone too — single form on the page.
 *   - Brand panel renders the actual logo (white via CSS filter on the
 *     dark teal aside) instead of the "INSOLECH" wordmark text.
 *   - Brand panel "Used by N clinics" tile reads "11+ clinics across the
 *     UAE" — synced with the marketing site's canonical figure.
 *   - Password input gets a show/hide eye toggle.
 *   - Optional "Remember me" checkbox. Default = true. Acts as a UX
 *     preference; Supabase always persists session via localStorage. We
 *     could refine to a sessionStorage / `expires_in` swap later, but
 *     for now this is purely a stated preference, NOT a plaintext-
 *     password storage trick (the legacy Lovable app did that and it's
 *     a flagged Phase 0 §9.1 security flaw — we will NEVER store the
 *     plaintext password).
 *
 * No sign-up tab. Per Phase 1 §08, INSOLECH is invitation-only — clinic
 * owners invite via the clinic-invitations flow.
 */

export function AuthPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { session, isLoading: authLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // R13c — if a valid session is already in localStorage (e.g. from a
  // previous attempt where the SDK promise hung but the underlying request
  // succeeded), redirect straight to /dashboard. Without this, signed-in
  // users who navigate to /auth see the form and assume they're not signed
  // in — which is what was happening in Rafael's normal Chrome window where
  // browser extensions were tripping the SDK's promise resolution.
  useEffect(() => {
    if (!authLoading && session) {
      navigate('/', { replace: true });
    }
  }, [authLoading, session, navigate]);

  return (
    <main className="min-h-screen flex bg-surface-base">
      {/* Left — brand panel */}
      <aside className="hidden lg:flex flex-col w-[44%] bg-surface-inverted text-text-onInverted relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-30"
          style={{
            background:
              'radial-gradient(circle at 30% 30%, #4F95B0 0%, transparent 60%), radial-gradient(circle at 70% 80%, #B85F2A 0%, transparent 50%)',
          }}
        />
        <div className="relative px-12 py-10 flex-1 flex flex-col">
          {/* R12 — logo links back to the marketing site. Standard
              B2B SaaS pattern; users naturally click the logo to "go
              home". The link wraps the same square logo treatment from
              R11 so the visual is unchanged. */}
          <a
            href={marketingUrl}
            className="self-start h-20 w-20 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 transition-opacity hover:opacity-80"
            aria-label="INSOLECH home"
          >
            <img
              src="/brand/logo-insolech.png"
              alt="INSOLECH"
              className="h-full w-full object-contain"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
          </a>
          <div className="my-auto max-w-md">
            <Eyebrow tone="clay" className="text-clay-300">
              MADE TO MOVE WITH YOU
            </Eyebrow>
            <p className="mt-6 text-3xl font-display font-medium leading-tight text-pretty">
              The platform that brings clinics, providers, and patients together, every
              prescription, every step.
            </p>
          </div>
          <p className="text-sm text-brand-200 max-w-md">
            Used by <span className="font-medium text-text-onBrand">11+ clinics</span> across
            the UAE.
          </p>
        </div>
      </aside>

      {/* Right — form panel */}
      <section className="flex-1 flex flex-col p-6 sm:p-10">
        {/* R13b — "Back to website" pill, normal flex child at the top
            of the panel (was absolute-positioned and overlapped the
            heading on shorter viewports). The form below uses m-auto
            so it still centers in the remaining vertical space. */}
        <a
          href={marketingUrl}
          className="self-start inline-flex items-center gap-2 h-9 ps-3 pe-4 rounded-full border border-border-default bg-surface-elevated text-sm font-medium text-text-secondary shadow-sm hover:bg-surface-base hover:text-text-primary hover:border-brand-200 hover:shadow transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 group"
        >
          <ArrowLeft
            className="h-4 w-4 rtl-flip transition-transform group-hover:-translate-x-0.5"
            aria-hidden="true"
          />
          <span>{t('back_to_website')}</span>
        </a>
        <div className="m-auto w-full max-w-md">
          <Heading as="h1" size="h3">
            {t('page_title_sign_in')}
          </Heading>
          <p className="mt-2 text-text-secondary">{t('page_subtitle_sign_in')}</p>

          <form
            className="flex flex-col gap-4 mt-8"
            onSubmit={async (e) => {
              e.preventDefault();
              setError(null);
              setLoading(true);
              // R13 — 15s watchdog. Some browser extensions (notably crypto
              // wallets like Polkadot.js) tamper with window.crypto and cause
              // the Supabase client to hang indefinitely. Without this, the
              // button sits on "Signing in..." forever with no feedback.
              const result = await Promise.race([
                supabase.auth.signInWithPassword({ email, password }),
                new Promise<{ error: { message: string } }>((resolve) =>
                  setTimeout(
                    () =>
                      resolve({
                        error: {
                          message:
                            "Sign-in timed out. A browser extension (e.g. a crypto wallet like Polkadot.js or MetaMask) may be interfering with this page. Try an Incognito window, or disable extensions on this site.",
                        },
                      }),
                    15000,
                  ),
                ),
              ]);
              const err = result.error;
              setLoading(false);
              if (err) {
                setError(err.message);
                return;
              }
              // Remember-me preference; sessionStorage swap is a future
              // enhancement. We NEVER store the plaintext password — the
              // legacy Lovable app's flaw (Phase 0 §9.1) stays buried.
              try {
                window.localStorage.setItem(
                  'insolech-remember-me',
                  rememberMe ? 'true' : 'false',
                );
              } catch {
                /* ignore quota / privacy mode */
              }
              navigate('/');
            }}
          >
            <FormField label={t('label_email')} required>
              <Input
                type="email"
                required
                autoComplete="email"
                placeholder={t('placeholder_email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </FormField>
            <FormField label={t('label_password')} required>
              <Input
                type={showPassword ? 'text' : 'password'}
                required
                autoComplete="current-password"
                placeholder={t('placeholder_password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                endAdornment={
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? t('hide_password') : t('show_password')}
                    aria-pressed={showPassword}
                    // p/-m trick: enlarges the 16px icon's hit area to ~44px
                    // on mobile without moving a pixel visually.
                    className="text-text-muted hover:text-text-primary transition-colors max-sm:p-3 max-sm:-m-3"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" aria-hidden="true" />
                    ) : (
                      <Eye className="h-4 w-4" aria-hidden="true" />
                    )}
                  </button>
                }
              />
            </FormField>
            <div className="flex items-center justify-between -mt-1">
              <label className="inline-flex items-center gap-2 text-sm text-text-secondary cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 max-sm:h-5 max-sm:w-5 rounded border-border-default text-brand-600 focus:ring-brand-500 focus:ring-offset-0 cursor-pointer"
                />
                {t('remember_me')}
              </label>
              <a
                href="/auth/reset"
                className="text-sm text-brand-600 hover:text-brand-700 max-sm:py-2 max-sm:-my-2"
              >
                {t('forgot_password')}
              </a>
            </div>
            {error && (
              <Alert tone="error" title="Sign-in failed">
                {error}
              </Alert>
            )}
            <Button type="submit" size="lg" disabled={loading}>
              {loading ? t('action_sign_in_loading') : t('action_sign_in')}
            </Button>
          </form>

          <p className="mt-8 text-xs text-text-muted text-center">{t('no_account_yet')}</p>
        </div>
      </section>
    </main>
  );
}
